import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { approvalsApi } from '../api/client';
import type { ApprovalRequest } from '../types';

// Loading skeleton
function ApprovalSkeleton() {
  return (
    <div className="card animate-pulse">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="h-4 w-24 bg-slate-700 rounded mb-3"></div>
          <div className="h-5 w-48 bg-slate-700 rounded mb-2"></div>
          <div className="h-4 w-full bg-slate-700 rounded mb-4"></div>
          <div className="h-20 bg-slate-700/50 rounded"></div>
        </div>
        <div className="flex gap-2 ml-4">
          <div className="h-10 w-24 bg-slate-700 rounded-lg"></div>
          <div className="h-10 w-24 bg-slate-700 rounded-lg"></div>
        </div>
      </div>
    </div>
  );
}

export function ApprovalQueue() {
  const queryClient = useQueryClient();
  const [selectedApproval, setSelectedApproval] = useState<ApprovalRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const { data: approvals, isLoading } = useQuery({
    queryKey: ['approvals'],
    queryFn: approvalsApi.list,
    refetchInterval: 5000,
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => approvalsApi.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      setSelectedApproval(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => approvalsApi.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      setSelectedApproval(null);
      setRejectionReason('');
    },
  });

  const pendingApprovals = approvals?.filter(a => a.status === 'pending') || [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Approval Queue</h2>
        </div>
        <ApprovalSkeleton />
        <ApprovalSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Approval Queue</h2>
        <span className="badge badge-warning">{pendingApprovals.length} pending</span>
      </div>

      {pendingApprovals.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-success-500/20 rounded-2xl flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-success-400" />
          </div>
          <p className="text-slate-400">No pending approvals</p>
          <p className="text-sm text-slate-500 mt-1">All actions are running smoothly</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingApprovals.map(approval => (
            <div key={approval.id} className="card glass-hover">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle
                      className={`w-5 h-5 ${approval.riskScore >= 70
                        ? 'text-danger-400'
                        : approval.riskScore >= 40
                          ? 'text-warning-400'
                          : 'text-success-400'
                        }`}
                    />
                    <span className={`badge ${approval.riskScore >= 70
                      ? 'risk-high'
                      : approval.riskScore >= 40
                        ? 'risk-medium'
                        : 'risk-low'
                      }`}>
                      Risk Score: {approval.riskScore}/100
                    </span>
                  </div>

                  <h3 className="font-semibold text-lg text-white mb-2">
                    {approval.action.tool}.{approval.action.operation}
                  </h3>
                  <p className="text-slate-400 mb-4">{approval.reason}</p>

                  <div className="bg-slate-800/50 rounded-lg p-4 mb-4 border border-slate-700/50">
                    <p className="text-sm font-medium text-slate-300 mb-2">Parameters:</p>
                    <pre className="text-xs text-slate-400 font-mono overflow-x-auto">
                      {JSON.stringify(approval.action.parameters, null, 2)}
                    </pre>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      {formatDistanceToNow(new Date(approval.timestamp), {
                        addSuffix: true,
                      })}
                    </span>
                    <span>Actor: {approval.actor.id}</span>
                    <span className="text-warning-400">
                      Expires:{' '}
                      {formatDistanceToNow(new Date(approval.approvalTimeout), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 ml-6">
                  <button
                    onClick={() =>
                      window.confirm('Approve this action?') && approveMutation.mutate(approval.id)
                    }
                    disabled={approveMutation.isPending}
                    className="btn btn-success flex items-center gap-2"
                  >
                    {approveMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Approve
                  </button>
                  <button
                    onClick={() => setSelectedApproval(approval)}
                    disabled={rejectMutation.isPending}
                    className="btn btn-danger flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rejection Modal */}
      {selectedApproval && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="glass rounded-2xl p-6 max-w-md w-full mx-4 animate-slide-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-danger-500/20 rounded-xl flex items-center justify-center">
                <XCircle className="w-5 h-5 text-danger-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Reject Approval</h3>
            </div>
            <p className="text-slate-400 mb-4">Please provide a reason for rejecting this action:</p>
            <textarea
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder="Explain why this action is being rejected..."
              className="input min-h-[120px] resize-none"
              autoFocus
            />
            <div className="flex gap-3 justify-end mt-4">
              <button
                onClick={() => {
                  setSelectedApproval(null);
                  setRejectionReason('');
                }}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  rejectMutation.mutate({
                    id: selectedApproval.id,
                    reason: rejectionReason,
                  })
                }
                disabled={!rejectionReason.trim() || rejectMutation.isPending}
                className="btn btn-danger flex items-center gap-2"
              >
                {rejectMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
